/**
 * useFormDeriveLiveValue
 *
 * Client-side preview of a Form Derived Value while the user types, layered by
 * `useFieldUI` over the persisted server value. Only the derive shapes reading
 * nothing but `FormVersion.fields` are answerable here; every other shape falls
 * through to what the server reconciler wrote, which is the intent, not a gap.
 *
 * The ROW PROJECTION arm is excluded permanently, not pending work. Two of its
 * five row-value arms are server-only by construction: `rowReferenceProp` needs
 * an `entities/{entityId}/records/{recordId}` fetch the caller resolves from the
 * source table's type document, and every arm reads whole ArTable rows this hook
 * never loads. A client that computed one would render a value the server
 * declines to write — a projection's output is a ROW SET, and the `drv~` id
 * namespace makes the reconciler the sole authority over which rows exist.
 * Projected cells render from what is stored, and closing this "gap" would put a
 * second writer on rows the server owns.
 *
 * The WHOLE field-only subset goes to `computeFormDerives`, never one entry:
 * `detectFormDeriveCycle` judges exactly the graph it is handed, so a narrower
 * slice would render a value the server refuses to compute. Subscription
 * discipline mirrors `useFieldUIScope` — one leaf per source field, no container
 * read, and nothing off `formVersion` at all until a derive targets this field.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';

import {
    computeFormDerives,
    createFormDeriveEngineConfig,
    formDeriveEntrySources,
    formDeriveEntryTableIds,
} from '#types';
import type { FieldId, Fields, FormDeriveEntry } from '#types';

import { formStore } from '../store';

/** `null` = no live answer; the caller keeps the persisted value. */
export type FormDeriveLiveValue = { value: unknown } | null;

/**
 * An entry this hook can answer at all: no ArTable on either side, and every
 * source read off `FormVersion.fields` — which excludes `referenceProp`, whose
 * record lives in another document. The TARGET is deliberately not tested, so
 * the engine sees every field-only edge and reaches the server's own cycle
 * verdict. `formDeriveEntryTableIds` is the direct spelling of the `tables: {}`
 * precondition below: an entry naming a table on either side would make
 * `computeFormDerives` throw on a table it was never handed.
 */
const isLiveComputable = (entry: FormDeriveEntry): boolean =>
    formDeriveEntryTableIds(entry).length === 0 &&
    formDeriveEntrySources(entry).every((source) => source.kind === 'field');

const sourceFieldIdsOf = (entries: FormDeriveEntry[]): FieldId[] => [
    ...new Set(
        entries.flatMap((entry) =>
            formDeriveEntrySources(entry).flatMap((source) =>
                source.kind === 'field' ? [source.fieldId] : [],
            ),
        ),
    ),
];

/**
 * What this agrees with the reconciler about, and what it does not. Agreement
 * holds for every shape the filter admits, INCLUDING a cycle between field-only
 * entries: the client's graph is a subgraph of the server's, so such a loop is
 * skipped in both and the field keeps its persisted value. It does NOT hold for
 * a cycle closing through an ArTable — those entries are filtered out, the loop
 * is invisible here, and this would show a live number the server declines to
 * write. Nothing on the client blocks that configuration today.
 *
 * The cost of handing over the whole subset: a derive-target field subscribes to
 * the union of source leaves across every field-only derive, not just its own.
 * Bounded by the number of derive targets rather than by the form's field count,
 * and every such field is a disabled input, so the extra re-render is cheap.
 *
 * `computeFormDerives` throws on input it cannot canonicalize; the filter makes
 * that unreachable through the admitted shapes, and the catch answers whatever
 * is left with the persisted value rather than a white screen.
 */
export const useFormDeriveLiveValue = (fieldId: FieldId, enabled: boolean): FormDeriveLiveValue => {
    const snap = useSnapshot(formStore.state);
    const formDerives = snap.formType.data?.formDerives;
    const formDeriveRev = snap.formType.data?.formDeriveRev;

    const config = useMemo(
        () => createFormDeriveEngineConfig({ formDerives, formDeriveRev }),
        [formDerives, formDeriveRev],
    );

    const entries = useMemo(() => config.entries.filter(isLiveComputable), [config]);

    const sourceFieldIds = useMemo(() => sourceFieldIdsOf(entries), [entries]);

    const targeted = entries.some(
        (entry) => 'fieldId' in entry.target && entry.target.fieldId === fieldId,
    );

    if (!enabled || !targeted) return null;

    /**
     * Read at render time, one leaf per source, so Valtio tracks exactly the
     * values these derives consume. Rebuilt into the minimal `Fields` shape the
     * engine expects rather than handed the live container. Below the guard on
     * purpose: a field no derive targets must read zero `formVersion` leaves.
     */
    const fields: Fields = Object.fromEntries(
        sourceFieldIds.map((sourceId) => [
            sourceId,
            { value: snap.formVersion.data?.fields?.[sourceId]?.value },
        ]),
    );

    try {
        const results = computeFormDerives({
            config: { entries, rev: config.rev },
            version: { fields },
            tables: {},
            records: {},
        });
        const computed = results.find(
            (result) => result.kind === 'field' && result.target.fieldId === fieldId,
        );
        return computed?.kind === 'field' ? { value: computed.value } : null;
    } catch {
        return null;
    }
};
